import React, { useCallback, useContext, useMemo, useState } from 'react';

import { createStyles, makeStyles } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';

import { ConfirmDialog, FilterPills } from '@vyce/core/src/components';
import { ListCompanyWorkersRequest } from '@vyce/core/src/api/types';
import { FilterSection, CompanyWorkerItem } from '@vyce/core/src/types';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { getCompanyWorkersfRequest } from '@vyce/core/src/api/companies';
import { removeEmployeeFromModuleRequest } from '@vyce/core/src/api/pay';
import { deleteTeamMembersFromModuleRequest } from '@vyce/core/src/api/teams';
import { deleteAllWorkerShiftsRequest } from '@vyce/core/src/api/time';
import { useBooleanState } from '@vyce/core/src/hooks';

import { useTypedSelector } from '../../../hooks';
import {
  USER_DOMAIN_OPTIONS,
  UserDomain,
  UserDomainOption,
  isUserDomain,
  ONBOARDING_FILTER_MAP,
  ONBOARDING_FILTER_VALUES,
} from '../constants';
import { UserList } from './UserList';

const useStyles = makeStyles(theme =>
  createStyles({
    chipsRow: {
      marginBottom: theme.spacing(2),
    },
  })
);

const parseDomainFromQuery = (search: string): UserDomain => {
  const params = new URLSearchParams(search);
  const value = params.get('domain');
  return isUserDomain(value) ? value : 'all';
};

const domainToOption = (domain: UserDomain): UserDomainOption =>
  USER_DOMAIN_OPTIONS.find(o => o.value === domain) ?? USER_DOMAIN_OPTIONS[0];

const buildFiltersSections = (domain: UserDomain): FilterSection[] | undefined => {
  const onboardingSection: FilterSection = {
    title: 'Onboarding',
    expanded: true,
    filters: [
      {
        type: 'select',
        multiple: false,
        label: 'Onboarding Status',
        field: 'onboarding_status',
        values: ONBOARDING_FILTER_VALUES,
        defaultValue: '',
      },
    ],
  };
  if (domain === 'payroll') {
    return [
      {
        title: 'Payroll Types',
        expanded: true,
        filters: [
          {
            type: 'select',
            multiple: false,
            label: 'Limited Company',
            field: 'is_limited_company',
            values: ['', 'Yes', 'No'],
            defaultValue: '',
          },
        ],
      },
      onboardingSection,
    ];
  }
  return [onboardingSection];
};

export const Users: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { handleServerError, showNotification } = useContext(NotificationContext);

  const [domain, setDomainState] = useState<UserDomain>(() => parseDomainFromQuery(location.search));
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [removeLoading, setRemoveLoadingTrue, setRemoveLoadingFalse] = useBooleanState(false);
  const [users, setUsers] = useState<CompanyWorkerItem[]>([]);
  const [userToRemove, setUserToRemove] = useState<CompanyWorkerItem>();
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useBooleanState(false);

  const { selectedCompany } = useTypedSelector(state => state.helper);

  const setDomain = useCallback(
    (next: UserDomain) => {
      setDomainState(next);
      const params = new URLSearchParams(location.search);
      if (next === 'all') {
        params.delete('domain');
      } else {
        params.set('domain', next);
      }
      const search = params.toString();
      history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    },
    [history, location.pathname, location.search]
  );

  const getUsers = useCallback(
    async (payload: ListCompanyWorkersRequest) => {
      if (!selectedCompany?.uuid) {
        return;
      }
      const module = domainToOption(domain).module;
      try {
        setLoadingTrue();
        const res = await getCompanyWorkersfRequest(selectedCompany.uuid, {
          ...payload,
          modules: module ? [module] : undefined,
        });
        setTotal(res.data.count);
        setUsers(res.data.items);
        setLoadingFalse();
      } catch (e) {
        setLoadingFalse();
        setUsers([]);
        handleServerError(e);
      }
    },
    [selectedCompany, domain, setLoadingTrue, setLoadingFalse, handleServerError]
  );

  const handleRemoveUserClick = useCallback(
    (user: CompanyWorkerItem) => {
      setUserToRemove(user);
      openConfirmDialog();
    },
    [openConfirmDialog]
  );

  const removeFromActiveDomain = useCallback(async () => {
    if (!selectedCompany?.uuid || !userToRemove?.user_id) {
      return;
    }
    try {
      setRemoveLoadingTrue();
      let successMessage: string | undefined;
      switch (domain) {
        case 'payroll': {
          const res = await removeEmployeeFromModuleRequest({
            token: '',
            userId: userToRemove.user_id,
            companyId: selectedCompany.uuid,
          });
          successMessage = res.data?.message;
          break;
        }
        case 'teams': {
          await deleteTeamMembersFromModuleRequest(selectedCompany.uuid, {
            members: [userToRemove.user_id],
          });
          successMessage = 'User removed from Teams module';
          break;
        }
        case 'time': {
          await deleteAllWorkerShiftsRequest(selectedCompany.uuid, userToRemove.user_id);
          successMessage = 'User removed from Time & Attendance module';
          break;
        }
        default:
          break;
      }
      setUsers(value => value.filter(user => user.user_id !== userToRemove.user_id));
      closeConfirmDialog();
      if (successMessage) {
        showNotification({ message: successMessage, options: { variant: 'success' } });
      }
      setRemoveLoadingFalse();
    } catch (e) {
      setRemoveLoadingFalse();
      handleServerError(e);
    }
  }, [
    selectedCompany,
    userToRemove,
    domain,
    setRemoveLoadingTrue,
    setRemoveLoadingFalse,
    closeConfirmDialog,
    showNotification,
    handleServerError,
  ]);

  const prepareFilters = useCallback(
    (filters: any): Partial<ListCompanyWorkersRequest> => {
      // Note: is_limited_company stays a UI-only no-op — the workers endpoint does not
      // expose business_types yet. Selection is still kept in the UI.
      const onboarding_status = ONBOARDING_FILTER_MAP[filters?.onboarding_status];
      // An empty selection must omit the key entirely — the field only exists on the
      // gateway once the onboarding feature ships there
      return onboarding_status ? { onboarding_status } : {};
    },
    []
  );

  const filtersSections = useMemo(() => buildFiltersSections(domain), [domain]);

  const activeOption = domainToOption(domain);
  const moduleName = activeOption.label;
  const showRemoveAction = domain !== 'all';
  const removeActionLabel =
    domain === 'all' ? undefined : `Remove from ${activeOption.label} module`;

  return (
    <>
      <FilterPills
        className={classes.chipsRow}
        options={USER_DOMAIN_OPTIONS}
        value={domain}
        onChange={setDomain}
        ariaLabel="User domain filter"
        cyPrefix="user-domain-chip"
      />

      <UserList
        moduleName={moduleName}
        users={users}
        loading={loading}
        getUsers={getUsers}
        total={total}
        handleRemoveUserClick={handleRemoveUserClick}
        filtersSections={filtersSections}
        prepareFilters={prepareFilters}
        showRemoveAction={showRemoveAction}
        removeActionLabel={removeActionLabel}
      />

      <ConfirmDialog
        handleClose={closeConfirmDialog}
        open={isConfirmDialogOpen}
        confirmText="Remove"
        cancelText="Cancel"
        title={`Are you sure you want to remove ${userToRemove?.first_name ?? ''} ${
          userToRemove?.last_name ?? ''
        } from ${activeOption.label} module?`}
        handleConfirm={removeFromActiveDomain}
        loading={removeLoading}
      />
    </>
  );
};
